import { useState, useEffect, useCallback, useRef } from 'react'

export type PermissionState = 'idle' | 'granted' | 'denied'

interface UseMicrophonePermissionOptions {
  autoRequest?: boolean
}

interface UseMicrophonePermissionReturn {
  permissionStatus: PermissionState
  mediaRecorder: MediaRecorder | null
  requestMicrophoneAccess: () => Promise<MediaRecorder | null>
}

/**
 * マイク権限とMediaRecorderを管理するフック
 *
 * @param isLoggedIn - ユーザーがログイン済みかどうか
 * @param options - オプション設定
 * @returns 権限状態とMediaRecorder
 */
export const useMicrophonePermission = (
  isLoggedIn: boolean,
  options: UseMicrophonePermissionOptions = {}
): UseMicrophonePermissionReturn => {
  const { autoRequest = true } = options

  const [permissionStatus, setPermissionStatus] =
    useState<PermissionState>('idle')
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const hasRequestedRef = useRef(false)
  const streamRef = useRef<MediaStream | null>(null)

  // マイク権限をリクエストしてMediaRecorderを初期化
  // 既存streamがactiveならそのまま再利用し、新しいMediaRecorderを作成して返す
  // 戻り値でMediaRecorderを返す（呼び出し側がstateの更新を待たずに利用できる）
  //
  // mimeType は AAC/MP4 を優先するが Firefox は MediaRecorder で audio/mp4 を未対応のため、
  // 構築時のみ別 try/catch で囲んで default (= WebM/Opus 等) に fallback する.
  // backend へは ffmpeg で MP3 に変換してから送るので中間 codec の違いは吸収される.
  const requestMicrophoneAccess =
    useCallback(async (): Promise<MediaRecorder | null> => {
      try {
        let stream = streamRef.current
        if (!stream || !stream.active) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          })
          streamRef.current = stream
        }
        let recorder: MediaRecorder
        try {
          recorder = new MediaRecorder(stream, { mimeType: 'audio/mp4' })
        } catch {
          recorder = new MediaRecorder(stream)
        }
        setMediaRecorder(recorder)
        setPermissionStatus('granted')
        return recorder
      } catch {
        setPermissionStatus('denied')
        return null
      }
    }, [])

  // マウント時にマイク権限をリクエスト（autoRequestが有効な場合）
  // React Compilerの要求に応じて、useEffect内で直接非同期処理を実行
  useEffect(() => {
    if (autoRequest && isLoggedIn && !hasRequestedRef.current) {
      hasRequestedRef.current = true
      // 直接実行（関数呼び出しではなく、インライン展開）
      const initializeMicrophone = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          })
          streamRef.current = stream
          // Firefox は audio/mp4 を MediaRecorder で未対応のため default に fallback.
          // backend は ffmpeg で MP3 化してから受けるので中間 codec の違いは吸収される.
          let recorder: MediaRecorder
          try {
            recorder = new MediaRecorder(stream, { mimeType: 'audio/mp4' })
          } catch {
            recorder = new MediaRecorder(stream)
          }
          setMediaRecorder(recorder)
          setPermissionStatus('granted')
        } catch {
          setPermissionStatus('denied')
        }
      }
      initializeMicrophone()
    }
  }, [autoRequest, isLoggedIn])

  return {
    permissionStatus,
    mediaRecorder,
    requestMicrophoneAccess,
  }
}
